import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutGrid, Gavel, Bell, ScrollText, Settings2, LogOut, ShieldCheck, Scale, Users, CalendarDays, ClipboardCheck, BarChart3, Scale as ScaleIcon, AlertOctagon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Layout() {
  const { user, logout, can, roleLabel, config } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications/mine").then((r) => setUnread(r.data.filter((n) => n.channel === "portal" && !n.read).length)).catch(() => {});
  }, []);

  const links = [
    { to: "/", label: "Case Dashboard", icon: LayoutGrid, show: true, id: "nav-dashboard" },
    { to: "/approvals", label: "Approval Queue", icon: Gavel, show: can("approvals.decide", "case.view_all"), id: "nav-approvals" },
    { to: "/calendar", label: "Listings Calendar", icon: CalendarDays, show: can("case.view_all"), id: "nav-calendar" },
    { to: "/hearings", label: "Hearing Checklists", icon: ClipboardCheck, show: can("case.view_all") && user.role !== "mediator", id: "nav-hearings" },
    { to: "/workload", label: "Member Workload", icon: ScaleIcon, show: can("case.view_all") && user.role !== "mediator", id: "nav-workload" },
    { to: "/reports", label: "Case Health Report", icon: BarChart3, show: can("case.edit", "config.manage"), id: "nav-reports" },
    { to: "/lodgement-rules", label: "Lodgement Rules", icon: ClipboardCheck, show: can("case.edit", "config.manage"), id: "nav-lodgement-rules" },
    { to: "/non-compliance", label: "Non-compliance", icon: AlertOctagon, show: can("case.view_all") && user.role !== "mediator", id: "nav-non-compliance" },
    { to: "/notifications", label: "Notifications", icon: Bell, show: true, id: "nav-notifications", badge: unread },
    { to: "/audit", label: "Audit Log", icon: ScrollText, show: can("audit.view"), id: "nav-audit" },
    { to: "/users", label: "User Management", icon: Users, show: can("users.manage"), id: "nav-users" },
    { to: "/config", label: "Configuration", icon: Settings2, show: can("config.manage"), id: "nav-config" },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-white flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded bg-blue-900 text-white flex items-center justify-center"><Scale className="h-4 w-4" /></div>
            <div><div className="font-bold text-slate-900 leading-tight">ResolveOS</div><div className="eyebrow">{config?.tribunal?.split(" ").filter((w) => /^[A-Z]/.test(w)).map((w) => w[0]).join("") || "VCAT"} · {config?.version}</div></div>
          </div>
        </div>
        <nav className="p-3 flex-1 space-y-0.5">
          {links.filter((l) => l.show).map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.id} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <l.icon className="h-4 w-4" /><span className="flex-1">{l.label}</span>
              {l.badge > 0 && <span className="mono text-[10px] bg-blue-900 text-white rounded-full px-1.5 py-0.5" data-testid="nav-unread-count">{l.badge}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
          <div className="font-semibold text-slate-700">AI before the judge,<br />not AI as the judge.</div>
          <div className="mt-1">All material decisions are made by authorised humans.</div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 h-14 px-6 flex items-center justify-between">
          <div className="text-sm text-slate-600">{config?.name}</div>
          <div className="flex items-center gap-4">
            <NavLink to="/security" className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:underline" data-testid="mfa-status"><ShieldCheck className="h-3.5 w-3.5" /> {user?.totp_enabled ? "Authenticator MFA" : "MFA verified (demo)"}</NavLink>
            <span className="text-xs bg-blue-50 text-blue-900 border border-blue-200 rounded px-2 py-1 font-semibold" data-testid="active-role-badge">{roleLabel}</span>
            <span className="text-sm text-slate-800" data-testid="user-name">{user?.name}</span>
            <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main className="flex-1"><Outlet /></main>
      </div>
    </div>
  );
}
